import React, { useEffect, useState } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { Link, useNavigate } from "react-router-dom";

interface IncomeStats {
 totalIncome: number;
 totalIncomeThisMonth: number;
 totalIncomeLastMonth: number;
 totalIncomeThisYear: number;
 averageOrderFee: number;
 totalOrders: number;
 growthRate: number;
 topStores: Array<{
  storeId: string;
  storeName: string;
  income: number;
  percentage: number;
 }>;
}

interface StoreContribution {
 storeId: string;
 storeName: string;
 income: number;
 orders: number;
 percentage: number;
 averageFee: number;
}

const PlatformIncome: React.FC = () => {
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const [stats, setStats] = useState<IncomeStats | null>(null);
 const [contributions, setContributions] = useState<StoreContribution[]>([]);
 const [loading, setLoading] = useState(true);
 const [filters, setFilters] = useState({
  startDate: "",
  endDate: "",
 });
 const [exporting, setExporting] = useState(false);

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  fetchData();
 }, [isAuthenticated, navigate]);

 const fetchData = async () => {
  try {
   setLoading(true);
   const [statsResponse, contributionsResponse] = await Promise.all([
    platformApiService.getIncomeStats(),
    platformApiService.getStoreContributions(
     filters.startDate ? new Date(filters.startDate) : undefined,
     filters.endDate ? new Date(filters.endDate) : undefined
    ),
   ]);

   setStats(statsResponse.data.stats);
   setContributions(contributionsResponse.data.contributions || []);
  } catch (error) {
   console.error("Error fetching income data:", error);
  } finally {
   setLoading(false);
  }
 };

 useEffect(() => {
  if (filters.startDate || filters.endDate) {
   fetchData();
  }
 }, [filters.startDate, filters.endDate]);

 const handleExport = async (format: "csv" | "json") => {
  try {
   setExporting(true);
   const response = await platformApiService.exportIncomeReport(
    filters.startDate ? new Date(filters.startDate) : undefined,
    filters.endDate ? new Date(filters.endDate) : undefined,
    format
   );

   if (format === "csv") {
    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `income-report-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
   } else {
    const blob = new Blob([JSON.stringify(response.data, null, 2)], {
     type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `income-report-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
   }
  } catch (error) {
   console.error("Error exporting income report:", error);
   alert("Failed to export income report");
  } finally {
   setExporting(false);
  }
 };

 if (loading && !stats) {
  return <div className="p-8">Loading income data...</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6">
    <h1 className="text-3xl font-bold mb-4">Platform Income</h1>

    {/* Filters */}
    <div className="bg-white shadow p-4 mb-6">
     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-1">
        Start Date
       </label>
       <input
        type="date"
        value={filters.startDate}
        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 "
       />
      </div>
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-1">
        End Date
       </label>
       <input
        type="date"
        value={filters.endDate}
        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 "
       />
      </div>
      <div className="flex items-end">
       <button
        onClick={() => setFilters({ startDate: "", endDate: "" })}
        className="px-4 py-2 border border-gray-300 hover:bg-gray-50"
       >
        Clear Filters
       </button>
      </div>
     </div>
    </div>

    {/* Summary Cards */}
    {stats && (
     <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white shadow p-4">
       <div className="text-sm text-gray-500">Total Income</div>
       <div className="text-2xl font-bold">${stats.totalIncome.toFixed(2)}</div>
      </div>
      <div className="bg-white shadow p-4">
       <div className="text-sm text-gray-500">This Month</div>
       <div className="text-2xl font-bold text-green-600">
        ${stats.totalIncomeThisMonth.toFixed(2)}
       </div>
       {stats.growthRate !== 0 && (
        <div className={`text-xs mt-1 ${stats.growthRate > 0 ? "text-green-600" : "text-red-600"}`}>
         {stats.growthRate > 0 ? "↑" : "↓"} {Math.abs(stats.growthRate).toFixed(1)}% from last month
        </div>
       )}
      </div>
      <div className="bg-white shadow p-4">
       <div className="text-sm text-gray-500">This Year</div>
       <div className="text-2xl font-bold">${stats.totalIncomeThisYear.toFixed(2)}</div>
      </div>
      <div className="bg-white shadow p-4">
       <div className="text-sm text-gray-500">Avg Order Fee</div>
       <div className="text-2xl font-bold">${stats.averageOrderFee.toFixed(2)}</div>
       <div className="text-xs text-gray-500 mt-1">{stats.totalOrders} orders</div>
      </div>
     </div>
    )}

    {/* Export Buttons */}
    <div className="mb-6 flex gap-2">
     <button
      onClick={() => handleExport("csv")}
      disabled={exporting}
      className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
     >
      {exporting ? "Exporting..." : "Export CSV"}
     </button>
     <button
      onClick={() => handleExport("json")}
      disabled={exporting}
      className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50"
     >
      {exporting ? "Exporting..." : "Export JSON"}
     </button>
    </div>
   </div>

   <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {/* Top Stores */}
    {stats && stats.topStores.length > 0 && (
     <div className="bg-white shadow p-6">
      <h2 className="text-xl font-bold mb-4">Top Stores by Income</h2>
      <div className="space-y-3">
       {stats.topStores.map((store, index) => (
        <div key={store.storeId} className="flex items-center justify-between">
         <div className="flex items-center">
          <span className="text-gray-500 w-6">{index + 1}.</span>
          <span className="font-medium">{store.storeName}</span>
         </div>
         <div className="text-right">
          <div className="font-semibold">${store.income.toFixed(2)}</div>
          <div className="text-xs text-gray-500">{store.percentage.toFixed(1)}%</div>
         </div>
        </div>
       ))}
      </div>
     </div>
    )}

    {/* Store Contributions */}
    <div className="bg-white shadow p-6">
     <h2 className="text-xl font-bold mb-4">Store Contributions</h2>
     {contributions.length === 0 ? (
      <div className="text-center py-8 text-gray-500">No contributions found</div>
     ) : (
      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Store
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Income
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Orders
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           %
          </th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {contributions.map((contribution) => (
          <tr key={contribution.storeId}>
           <td className="px-4 py-3 whitespace-nowrap">
            <Link
             to={`/platform/stores/${contribution.storeId}`}
             className="text-indigo-600 hover:text-indigo-900 font-medium"
            >
             {contribution.storeName}
            </Link>
           </td>
           <td className="px-4 py-3 whitespace-nowrap font-semibold">
            ${contribution.income.toFixed(2)}
           </td>
           <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
            {contribution.orders}
           </td>
           <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
            {contribution.percentage.toFixed(1)}%
           </td>
          </tr>
         ))}
        </tbody>
       </table>
      </div>
     )}
    </div>
   </div>
  </div>
 );
};

export default PlatformIncome;

